import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { X, Plus, CalendarDays, ListTodo } from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { errMsg } from '../SupportCoordination/kit';
import { expandAll } from '../SupportCoordination/calendar/calendarMath';
import { TaskRows, TaskSheet, useTasks, toggleDone, ymdLocal, groupOf } from './TasksApp';

// My day, beside the mail (View › My day, as in Outlook): today's appointments on your calendar
// (and your Outlook events), and your tasks that are due or overdue.
export default function MyDay({ onClose }) {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const today = ymdLocal();
  const [events, setEvents] = useState(null);
  const { rows, setRows, load } = useTasks({ scope: 'mine', state: 'open' });
  const [adding, setAdding] = useState('');
  const [editing, setEditing] = useState(null);
  const loadEvents = useCallback(() => api.get('/calendar/events', { params: { from: today, to: today } }).then((r) => setEvents(r.data)).catch(() => setEvents({ appointments: [], outside: [] })), [today]);
  useEffect(() => { loadEvents(); }, [loadEvents]);
  const mine = useMemo(() => {
    if (!events) return [];
    const start = new Date(); start.setHours(0, 0, 0, 0);
    const end = new Date(start); end.setDate(end.getDate() + 1); end.setMinutes(-1);
    const email = String((user && user.email) || '').toLowerCase();
    const appts = expandAll((events.appointments || []).filter((a) => String(a.coordinator_id || '') === String(user && user.id) || (a.attendees || []).some((x) => x.email === email)), start, end, {})
      .map((o) => ({ key: o.key, title: o.title, start: o.start, end: o.end, all_day: o.all_day, where: o.location, outside: false }));
    const outside = (events.outside || []).map((x) => ({ key: `out:${x.remote_id}`, title: x.title, start: new Date(x.start_at), end: new Date(x.end_at), all_day: x.all_day, outside: true }))
      .filter((o) => o.start < end && o.end > start);
    return [...appts, ...outside].sort((a, b) => a.start - b.start);
  }, [events, user]);
  const due = useMemo(() => (rows || []).filter((t) => t.due_date && ['overdue', 'today'].includes(groupOf(t.due_date, today))), [rows, today]);
  const add = async (e) => {
    e.preventDefault();
    const title = adding.trim();
    if (!title) return;
    setAdding('');
    try { await api.post('/email-tasks', { title, due_date: today }); load(); } catch (err) { toast.error(errMsg(err)); setAdding(title); }
  };
  const hm = (d) => d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' });
  return (
    <aside className="hidden lg:flex w-[300px] flex-shrink-0 flex-col border-l border-slate-200 bg-[#fafafa] min-h-0" aria-label="My day">
      <div className="flex items-center gap-2 px-4 pt-3 pb-2">
        <div className="flex-1">
          <p className="text-[15px] font-semibold text-slate-900">My day</p>
          <p className="text-[12px] text-slate-500">{new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
        </div>
        <button type="button" onClick={onClose} aria-label="Close My day" className="w-8 h-8 rounded-md flex items-center justify-center text-slate-500 hover:bg-slate-200/60"><X size={16} aria-hidden /></button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto mail-scroll px-3 pb-4 space-y-5">
        <section>
          <button type="button" onClick={() => navigate(`/mail?view=calendar&cv=day&d=${today}`)} className="flex items-center gap-1.5 px-1 mb-1.5 text-[12px] font-semibold uppercase tracking-wider text-slate-500 hover:text-[#0f6cbd]">
            <CalendarDays size={13} aria-hidden /> Calendar
          </button>
          {events === null ? <p className="px-1 text-[12.5px] text-slate-500">Loading…</p> : !mine.length ? <p className="px-1 text-[12.5px] text-slate-500">Nothing booked today.</p> : (
            <ul className="space-y-1">
              {mine.map((o) => (
                <li key={o.key} className="flex gap-2.5 rounded-lg bg-white ring-1 ring-slate-200/80 px-2.5 py-2">
                  <span className={`w-1 rounded-full flex-shrink-0 ${o.outside ? 'bg-slate-400' : 'bg-[#0f6cbd]'}`} aria-hidden />
                  <div className="min-w-0">
                    <p className="text-[13px] text-slate-900 truncate">{o.title}</p>
                    <p className="text-[11.5px] text-slate-500">{o.all_day ? 'All day' : `${hm(o.start)} – ${hm(o.end)}`}{o.outside ? ' · Outlook' : ''}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section>
          <button type="button" onClick={() => navigate('/mail?view=tasks')} className="flex items-center gap-1.5 px-1 mb-1.5 text-[12px] font-semibold uppercase tracking-wider text-slate-500 hover:text-[#0f6cbd]">
            <ListTodo size={13} aria-hidden /> Due today{due.some((t) => groupOf(t.due_date, today) === 'overdue') ? ' and overdue' : ''}
          </button>
          <form onSubmit={add} className="flex items-center gap-2 h-9 px-2.5 mb-2 rounded-lg bg-white ring-1 ring-slate-200 focus-within:ring-2 focus-within:ring-[#0f6cbd]/60">
            <Plus size={15} className="text-[#0f6cbd]" aria-hidden />
            <input value={adding} onChange={(e) => setAdding(e.target.value)} placeholder="Add a task for today" aria-label="Add a task for today" className="flex-1 min-w-0 bg-transparent outline-none text-[13px]" />
          </form>
          {rows === null ? <p className="px-1 text-[12.5px] text-slate-500">Loading…</p> : !due.length ? <p className="px-1 text-[12.5px] text-slate-500">Nothing due. </p> : (
            <TaskRows tasks={due} today={today} compact onToggle={(t) => toggleDone(t, setRows)} onOpen={setEditing} />
          )}
        </section>
      </div>
      <TaskSheet task={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </aside>
  );
}
